import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert, Filter, Download } from "lucide-react";
import { toast } from "sonner";
import { reportsApi, formatAuDate } from "../../lib/events";
import { extractError } from "../../lib/api";

const ROLE_LABEL = {
  platform_admin: "Admin",
  event_owner: "Owner",
  team_member: "Team member",
};

function resourceText(row) {
  if (row.resource_type === "ticket") {
    const bits = [];
    if (row.ticket_id) bits.push(`Ticket ${String(row.ticket_id).slice(0, 8)}`);
    if (row.order_id) bits.push(`Order ${String(row.order_id).slice(0, 8)}`);
    return bits.join(" · ") || "Ticket";
  }
  if (row.export_type) return `${row.resource_type} (${String(row.export_type).toUpperCase()})`;
  return row.resource_type || "—";
}

/**
 * PiiAccessLog — owner/admin accountability view of attendee-PII activity.
 * Fetches lazily. No raw attendee PII is ever shown here.
 */
export function PiiAccessLog({ eventId, hideTeamPii = false, canEditPolicy = false, onPolicyChanged }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [action, setAction] = useState("");
  const [actorId, setActorId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const togglePolicy = useCallback(async () => {
    const next = !hideTeamPii;
    setSavingPolicy(true);
    try {
      await reportsApi.setPiiPolicy(eventId, next);
      toast.success(next
        ? "Attendee contacts are now hidden from all team members."
        : "Attendee contacts follow individual permissions again.");
      if (onPolicyChanged) await onPolicyChanged();
    } catch (e) {
      toast.error(extractError(e) || "Could not update attendee contact privacy.");
    } finally {
      setSavingPolicy(false);
    }
  }, [eventId, hideTeamPii, onPolicyChanged]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.piiAccessLog(eventId, {
        action, actor_id: actorId, date_from: dateFrom, date_to: dateTo, limit: 100,
      });
      setData(res);
      setError("");
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [eventId, action, actorId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const { blob, headers } = await reportsApi.piiAccessLogCsv(eventId, {
        action, actor_id: actorId, date_from: dateFrom, date_to: dateTo,
      });
      let filename = `privacy-activity-${eventId}.csv`;
      const cd = headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Privacy log exported.");
    } catch (e) {
      toast.error(extractError(e) || "Could not export the privacy log.");
    } finally {
      setDownloading(false);
    }
  }, [eventId, action, actorId, dateFrom, dateTo]);

  const labels = data?.action_labels || {};
  const items = data?.items || [];
  const summary = data?.summary;

  const cards = summary ? [
    { key: "contact_views", label: "Contact Views", value: summary.contact_views },
    { key: "exports", label: "Exports", value: summary.exports },
    { key: "attendee_edits", label: "Attendee Edits", value: summary.attendee_edits },
    { key: "ticket_resends", label: "Ticket Resends", value: summary.ticket_resends },
    { key: "unique_users", label: "Unique Users", value: summary.unique_users },
  ] : [];

  return (
    <div className="gt-card" data-testid="pii-access-log">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="gt-label flex items-center gap-1"><ShieldCheck size={13} /> Privacy</div>
          <h3 className="font-heading text-xl font-black tracking-tighter mt-1">Attendee PII activity</h3>
          <p className="text-xs text-[var(--gt-ink-3)] mt-1">
            Who viewed, exported, edited or resent attendee contact details. No
            contact details are shown here.
          </p>
        </div>
        <div
          className={`shrink-0 border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${hideTeamPii ? "border-[var(--gt-accent)] text-[var(--gt-accent)]" : "border-[var(--gt-ink)] text-[var(--gt-ink)]"}`}
          data-testid="pii-team-access-status"
        >
          {hideTeamPii ? <ShieldAlert size={13} /> : <ShieldCheck size={13} />}
          Team contact access: {hideTeamPii ? "Restricted" : "Individual permissions"}
        </div>
      </div>

      {canEditPolicy && (
        <div className="border-2 border-[var(--gt-ink)] p-4 mb-6" data-testid="pii-policy-control">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-heading text-base font-black tracking-tight">Attendee Contact Privacy</div>
              <p className="text-xs text-[var(--gt-ink-3)] mt-1 max-w-2xl">
                When enabled, attendee contact details are hidden from all Event
                Managers and team members, even if they individually have Customer
                details permission. Event owners and platform administrators retain access.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={hideTeamPii}
              onClick={togglePolicy}
              disabled={savingPolicy}
              data-testid="pii-policy-toggle"
              className={`relative shrink-0 w-14 h-8 border-2 border-[var(--gt-ink)] transition-colors ${hideTeamPii ? "bg-[var(--gt-accent)]" : "bg-transparent"} disabled:opacity-60`}
              title="Hide attendee email and phone from all team members"
            >
              <span
                className={`absolute top-0.5 h-6 w-6 bg-[var(--gt-ink)] transition-all ${hideTeamPii ? "left-6" : "left-0.5"}`}
              />
            </button>
          </div>
          <div className="text-[11px] text-[var(--gt-ink-3)] mt-2">
            {savingPolicy ? "Saving…" : (hideTeamPii
              ? "Hide attendee email and phone from all team members — ON"
              : "Hide attendee email and phone from all team members — OFF")}
          </div>
        </div>
      )}

      {summary && (
        <div className="mb-6" data-testid="pii-summary">
          <div className="gt-label mb-2">Privacy Activity — Last 7 Days</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {cards.map((c) => (
              <div key={c.key} className="border-2 border-[var(--gt-ink)] p-3" data-testid={`pii-summary-${c.key}`}>
                <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">{c.label}</div>
                <div className="font-heading text-2xl font-black tracking-tighter leading-none mt-1" data-testid={`pii-summary-${c.key}-value`}>
                  {c.value ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="gt-label mb-1 block">Action</label>
          <select
            className="gt-input py-1.5 text-xs"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            data-testid="pii-log-filter-action"
          >
            <option value="">All actions</option>
            {Object.entries(labels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="gt-label mb-1 block">User</label>
          <select
            className="gt-input py-1.5 text-xs"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
            data-testid="pii-log-filter-user"
          >
            <option value="">All users</option>
            {(data?.actors || []).map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.email || a.id.slice(0, 8)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="gt-label mb-1 block">From</label>
          <input type="date" className="gt-input py-1.5 text-xs" value={dateFrom}
                 onChange={(e) => setDateFrom(e.target.value)} data-testid="pii-log-filter-from" />
        </div>
        <div>
          <label className="gt-label mb-1 block">To</label>
          <input type="date" className="gt-input py-1.5 text-xs" value={dateTo}
                 onChange={(e) => setDateTo(e.target.value)} data-testid="pii-log-filter-to" />
        </div>
        {(action || actorId || dateFrom || dateTo) && (
          <button
            type="button"
            className="gt-btn-secondary text-xs py-1.5 inline-flex items-center gap-1"
            onClick={() => { setAction(""); setActorId(""); setDateFrom(""); setDateTo(""); }}
            data-testid="pii-log-filter-clear"
          >
            <Filter size={12} /> Clear
          </button>
        )}
        <div className="ml-auto">
          <button
            type="button"
            className="gt-btn-primary text-xs py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
            onClick={exportCsv}
            disabled={downloading}
            data-testid="pii-log-export-csv"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            {downloading ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex items-center gap-2 text-[var(--gt-ink-2)] text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading privacy activity…
        </div>
      ) : error ? (
        <div className="py-6 text-sm text-[var(--gt-accent)]" data-testid="pii-log-error">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                <th className="py-2 pr-3">Time</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Action</th>
                <th className="py-2 pr-3">Resource</th>
                <th className="py-2 pr-3 text-right">Records</th>
              </tr>
            </thead>
            <tbody data-testid="pii-log-table">
              {items.map((r) => (
                <tr key={r.id} className="border-b border-[var(--gt-line)]" data-testid={`pii-log-row-${r.id}`}>
                  <td className="py-3 pr-3 text-xs text-[var(--gt-ink-2)] whitespace-nowrap">{formatAuDate(r.created_at)}</td>
                  <td className="py-3 pr-3">{r.actor_name || r.actor_email || "—"}</td>
                  <td className="py-3 pr-3 text-xs">{ROLE_LABEL[r.actor_role] || r.actor_role || "—"}</td>
                  <td className="py-3 pr-3">{r.action_label}</td>
                  <td className="py-3 pr-3 text-xs text-[var(--gt-ink-2)]">{resourceText(r)}</td>
                  <td className="py-3 pr-3 text-right font-mono">{r.records_count ?? "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-sm text-[var(--gt-ink-3)]" data-testid="pii-log-empty">No privacy activity recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PiiAccessLog;
